import { useForm, SubmitHandler } from 'react-hook-form';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import { signInCredentialsSchema, SignInCredentials } from '@interfaces/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import useToastContext from '@contexts/ToastContext/hook';
import { Button } from '@vibTheme/components/_base/Button';
import { Link } from '@vibTheme/components/_base/Link';
import { InputPassword } from '@vibTheme/components/_base/Input';
import { Typography } from '@vibTheme/components/_base/Typography';
import InputDocument from '@vibTheme/components/_base/Input/InputDocument';
import { clearSelectedGroups } from '@repository/selectedGroups';
import { clearSelectedOffers } from '@repository/selectedOffers';
import { clearSelectedFilters } from '@repository/selectedFilters';
import { clearWelcomeDialogPostponement } from '@repository/welcomeDialog';
import { removeAttributionFromLocalStorage } from '@utils/captureAttribution';
import { VibErrorMessages } from '@interfaces/vibError';
import { resolveInternalCallback } from '@utils/resolveInternalCallback';

export const SignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToastContext();
  const router = useRouter();

  /**
   * A recuperação de senha carrega o DESTINO junto.
   *
   * Quem chega ao login por um convite e não lembra a senha sai por aqui — e o destino morria: a
   * página de recuperação volta para `/auth/signIn` literal, então o login seguinte terminava em `/`
   * com o convite pendente e o cookie de capacidade correndo.
   */
  const urlDeRecuperacao = () => {
    const destino = resolveInternalCallback(
      router.query.callbackUrl,
      typeof window === 'undefined' ? '' : window.location.origin,
    );

    return destino
      ? `/auth/resetPassword?callbackUrl=${encodeURIComponent(destino)}`
      : '/auth/resetPassword';
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInCredentials>({
    resolver: zodResolver(signInCredentialsSchema),
  });

  const handleSignIn: SubmitHandler<SignInCredentials> = async (data) => {
    const { document, password } = data;

    setIsLoading(true);
    try {
      clearSelectedGroups();
      clearSelectedOffers();
      clearSelectedFilters();
      const response = await signIn('credentials', {
        document,
        password,
        redirect: false,
      });
      if (response?.error) throw new Error(response.error);
      clearWelcomeDialogPostponement();

      /*
        Login de conta que já existe encerra a origem guardada: aquela campanha trouxe uma sessão,
        não um cadastro. Sem isto ela ficaria no navegador até vencer o prazo — pronta para ser
        carimbada num cadastro futuro, que pode nem ser desta pessoa, e para bloquear a campanha
        seguinte pela regra de primeiro toque.

        Depois do sucesso, e não antes: um login que falha não pode custar a atribuição de um
        cadastro que ainda vai acontecer.
      */
      removeAttributionFromLocalStorage();

      /*
        `resolveInternalCallback` no lugar da comparação com a origem.

        A checagem antiga aceitava só a forma ABSOLUTA de mesma origem — que é a que o `signIn()`
        produz —, e recusava a relativa. O cadastro manda a relativa (`/convite`) quando o login
        automático falha, e ela caía fora: a pessoa terminava em `/` com o convite pendente e o
        cookie de capacidade correndo.

        O resolvedor aceita as duas formas e continua recusando redirect aberto, resolvendo contra a
        origem em vez de comparar prefixos.
      */
      const callbackUrl =
        resolveInternalCallback(
          router.query.callbackUrl,
          window.location.origin,
        ) ?? '/';

      router.push(callbackUrl);
    } catch (err: any) {
      err.message === VibErrorMessages.BLOCKED_PLATFORM_USER &&
        router.push(urlDeRecuperacao());

      toast.handleError(err, err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <Typography component="h1" className="pb-main">
        Login
      </Typography>
      <form
        onSubmit={handleSubmit(handleSignIn)}
        className="flex flex-col gap-lg items-center"
      >
        <InputDocument
          id="login-input-document"
          control={control}
          name="document"
          label="CPF/CNPJ"
          placeholder="ex: 123.456.789-00"
          fullWidth
          autoFocus
          error={!!errors.document}
          helperText={errors.document?.message}
          data-testid="login-input-document"
        />
        <InputPassword
          id="login-input-password"
          InputLabelProps={{ shrink: true }}
          name="password"
          control={control}
          label="Senha"
          error={!!errors.password}
          helperText={errors.password?.message}
        />
        <Link id="login-link-resetpassword" href={urlDeRecuperacao()}>
          Esqueci minha senha
        </Link>
        <Button
          id="login-button-submit"
          type="submit"
          loading={isLoading}
          className="w-[70%]"
        >
          Entrar
        </Button>
      </form>
      <div className="mt-32 flex items-center place-content-center">
        <Typography component="h6" key="h6">
          Não possui cadastro?{' '}
          {/*
            O `callbackUrl` viaja para o cadastro. Quem chega aqui por uma página privada — o convite
            de colaborador é o caso — não tem conta ainda, e o caminho é signIn -> cadastro: sem
            repassar, o destino se perde no meio e a pessoa termina o cadastro em outro lugar.
          */}
          <Link
            id="login-link-register"
            href={
              router.query.callbackUrl
                ? `/auth/register?callbackUrl=${encodeURIComponent(
                    String(router.query.callbackUrl),
                  )}`
                : '/auth/register'
            }
          >
            Clique aqui
          </Link>
        </Typography>
      </div>
    </Fragment>
  );
};
